import React, { useCallback, useRef } from 'react';
import classnames from 'classnames';
import { useRemoteConfig } from '@funtech-int/yango-shared';
import { useI18n as useI18nManager } from '@funtech-int/rival-react';

import { Button } from '@/shared/components/Button/Button';
import { ArrowBottom } from '@/shared/components/ArrowBottom/ArrowBottom';
import { renderWithBr } from '@/shared/lib/renderWithBr';
import { useLoginUrl } from '@/shared/hooks/useLoginUrl';
import { ShowcaseId } from '@/shared/lib/analytics/showcase';

import { getLoginwallFallbackTexts } from '../../lib/getLoginwallFallbackTexts';
import { remoteConfigSchema } from '../../lib/remoteConfig';
import { HomeShowcase } from '../HomeShowcase/HomeShowcase';
import { HomeShowcaseSection } from '../HomeShowcaseSection/HomeShowcaseSection';
import { HomeShowcaseHeader } from '../HomeShowcaseHeader/HomeShowcaseHeader';
import { StaticCover } from '../StaticCover/StaticCover';
import { useI18n } from '../../hooks/useOnboardingI18n';

import styles from './LoginwallMainSection.module.css';

const useLoginwallMainSection = () => {
    const remoteConfig = useRemoteConfig(remoteConfigSchema);
    const { lang } = useI18nManager().locale.get();
    const loginwallConfig = remoteConfig.get('loginwall_config');
    const backgroundImageUrl = loginwallConfig?.backgroundImageUrl;
    const remoteTitle = loginwallConfig?.title;
    const remoteSubtitle = loginwallConfig?.subtitle;

    const title = remoteTitle?.[lang];
    const subtitle = remoteSubtitle?.[lang];
    const backgroundUrl = backgroundImageUrl?.[lang] || '';
    const loginwallShowcaseId = loginwallConfig?.loginwallShowcaseId;

    return {
        backgroundUrl,
        title,
        subtitle,
        loginwallShowcaseId,
    };
};

export const LoginwallMainSection = () => {
    const { backgroundUrl, title, subtitle, loginwallShowcaseId } = useLoginwallMainSection();
    const i18n = useI18n();
    const fallbackTexts = getLoginwallFallbackTexts(i18n);

    const mainRef = useRef<HTMLDivElement>(null);

    const scrollToNextSection = useCallback(() => {
        const heightMain =
            (mainRef.current && mainRef.current.offsetHeight + mainRef.current.offsetTop) || 0;

        window.scrollTo({ top: heightMain, behavior: 'smooth' });
    }, [mainRef]);

    const loginUrl = useLoginUrl();

    return (
        <div className={classnames(styles.root)}>
            <div className={styles.loginBlock} ref={mainRef}>
                <StaticCover backgroundUrl={backgroundUrl} />
                <div className={styles.loginWrapper}>
                    <h1 className={styles.title}>{renderWithBr(title, fallbackTexts.title)}</h1>
                    <p className={styles.subTitle}>
                        {renderWithBr(subtitle, fallbackTexts.subtitle)}
                    </p>
                    <Button
                        as="a"
                        variant="accent"
                        size={60}
                        href={loginUrl.toString()}
                        className={classnames(styles.loginButton)}
                        data-test-id="LoginwallLoginButton"
                    >
                        {i18n('loginwall_button')}
                    </Button>
                </div>
                <div className={styles.arrowBottom}>
                    <ArrowBottom
                        onScrollTo={scrollToNextSection}
                        withAnimation={false}
                        title={i18n('onboarding__arrow_button_text')}
                    />
                </div>
            </div>
            <HomeShowcaseSection className={styles.showcase}>
                <HomeShowcase
                    id={loginwallShowcaseId ?? ShowcaseId.LOGINWALL}
                    header={<HomeShowcaseHeader />}
                    footer={
                        <div className={classnames(styles.loginInset)}>
                            <Button
                                as="a"
                                variant="accent"
                                size={60}
                                href={loginUrl.toString()}
                                className={classnames(styles.loginButton)}
                            >
                                {i18n('loginwall_button')}
                            </Button>
                        </div>
                    }
                />
            </HomeShowcaseSection>
        </div>
    );
};
